import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function AdminAnalytics() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/analytics/summary?days=${days}`);
            setData(data);
        } catch (e) {
            toast.error(e?.message || "Failed to load analytics");
        } finally { setLoading(false); }
    }

    useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

    const funnel = data?.funnel || {};
    const funnelStages = [
        ["donate_page_view", "Donation Page"],
        ["amount_selected", "Amount Selected"],
        ["frequency_selected", "Frequency Selected"],
        ["payment_method_selected", "Payment Method"],
        ["confirmation_submitted", "Confirmation Submitted"],
    ];
    const funnelTop = funnel.donate_page_view || 0;
    const conversion = funnelTop > 0 ? Math.round(((funnel.confirmation_submitted || 0) / funnelTop) * 100) : 0;

    return (
        <div data-testid="admin-analytics">
            <div className="flex flex-wrap justify-between items-end gap-3">
                <div>
                    <div className="gsf-eyebrow">Donor Intent & Conversion</div>
                    <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold" data-testid="analytics-title">Analytics</h1>
                    {data && <p className="text-xs text-gray-500 mt-1">Last {data.range_days} days · {data.total_events.toLocaleString()} events · conversion {conversion}%</p>}
                </div>
                <div className="flex gap-2">
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white" data-testid="range-select">
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={365}>Last year</option>
                    </select>
                    <button onClick={load} className="gsf-btn-outline text-sm" data-testid="refresh"><RefreshCw size={14} /> Refresh</button>
                </div>
            </div>

            {loading && <p className="mt-8 text-gray-500 text-sm">Loading…</p>}

            {data && (
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="gsf-card p-6 lg:col-span-3" data-testid="funnel-card">
                        <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Funnel</h2>
                        <div className="mt-4 space-y-2">
                            {funnelStages.map(([k, label]) => {
                                const n = funnel[k] || 0;
                                const pct = funnelTop > 0 ? Math.round((n / funnelTop) * 100) : 0;
                                return (
                                    <div key={k} data-testid={`funnel-${k}`}>
                                        <div className="flex justify-between text-xs text-gray-600"><span>{label}</span><span>{n.toLocaleString()} · {pct}%</span></div>
                                        <div className="h-3 bg-gray-100 rounded overflow-hidden">
                                            <div className="h-3 bg-[color:var(--gsf-green-700)]" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <CountCard title="Popular Amounts (USD)" testid="amounts-card" items={data.amounts} formatKey={(k) => `US$${Number(k).toLocaleString()}`} />
                    <CountCard title="Payment Methods" testid="methods-card" items={data.methods} />
                    <CountCard title="Frequency" testid="freq-card" items={data.frequencies} />
                    <CountCard title="Devices" testid="devices-card" items={data.devices} />
                    <CountCard title="Countries" testid="countries-card" items={data.countries} />
                    <CountCard title="Errors" testid="errors-card" items={data.errors} />

                    <div className="gsf-card p-6 lg:col-span-3" data-testid="perday-card">
                        <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Events per day</h2>
                        <div className="mt-4 flex items-end gap-1 h-32">
                            {(data.per_day || []).map((d) => {
                                const max = Math.max(...(data.per_day || []).map((x) => x.n), 1);
                                const h = Math.max(2, Math.round((d.n / max) * 100));
                                return <div key={d.date} title={`${d.date}: ${d.n}`} className="flex-1 bg-[color:var(--gsf-green-700)]/70 rounded-t" style={{ height: `${h}%` }} />;
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CountCard({ title, items, testid, formatKey }) {
    const entries = Object.entries(items || {});
    const total = entries.reduce((s, [, n]) => s + n, 0);
    return (
        <div className="gsf-card p-6" data-testid={testid}>
            <h2 className="font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">{title}</h2>
            {entries.length === 0 ? <p className="mt-2 text-xs text-gray-500">No data yet.</p> : (
                <ul className="mt-3 space-y-1.5 text-sm">
                    {entries.map(([k, n]) => {
                        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
                        return (
                            <li key={k} className="flex justify-between gap-2">
                                <span className="truncate">{formatKey ? formatKey(k) : k}</span>
                                <span className="text-gray-500 whitespace-nowrap">{n.toLocaleString()} · {pct}%</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
